import { z } from "zod";

export const seedanceModelSchema = z.enum([
  "doubao-seedance-2-0-260128",
  "doubao-seedance-2-0-fast-260128"
]);
export const seedanceRatioSchema = z.enum([
  "16:9",
  "21:9",
  "9:16",
  "1:1",
  "4:3",
  "3:4",
  "adaptive"
]);
export const seedanceResolutionSchema = z.enum(["480p", "720p", "1080p"]);
export const assetKindSchema = z.enum(["image", "video", "audio"]);
export const decimalByteCountSchema = z.string().regex(/^(0|[1-9]\d*)$/u);
export const referenceRoleSchema = z.enum([
  "reference",
  "reference_video",
  "first_frame",
  "last_frame",
  "subject",
  "scene",
  "style",
  "composition",
  "action",
  "camera",
  "extend_video",
  "audio_reference",
  "custom"
]);

export type SeedanceModel = z.infer<typeof seedanceModelSchema>;
export type SeedanceRatio = z.infer<typeof seedanceRatioSchema>;
export type SeedanceResolution = z.infer<typeof seedanceResolutionSchema>;
export type AssetKind = z.infer<typeof assetKindSchema>;
export type ReferenceRole = z.infer<typeof referenceRoleSchema>;

export const seedanceCapabilityRangeSchema = z.strictObject({
  minimum: z.number().int(),
  maximum: z.number().int(),
  step: z.number().int().positive()
});
export const seedanceSeedRangeSchema = z.strictObject({
  minimum: z.number().int(),
  maximum: z.number().int()
});
export const seedanceFileLimitsBytesSchema = z.strictObject({
  image: z.number().int().positive(),
  video: z.number().int().positive(),
  audio: z.number().int().positive()
});
export const seedanceModelCapabilitySchema = z.strictObject({
  id: seedanceModelSchema,
  resolutions: z.array(seedanceResolutionSchema).min(1),
  durationSeconds: seedanceCapabilityRangeSchema,
  allowedRoles: z.strictObject({
    image: z.array(referenceRoleSchema).min(1),
    video: z.array(referenceRoleSchema).min(1),
    audio: z.array(referenceRoleSchema).min(1)
  }),
  maxReferences: z.number().int().positive(),
  maxReferencesByKind: z.strictObject({
    image: z.number().int().positive(),
    video: z.number().int().positive(),
    audio: z.number().int().positive()
  }),
  supportsAudio: z.boolean(),
  supportsWatermark: z.boolean()
});
export const seedanceCapabilityCatalogSchema = z.strictObject({
  capabilityVersion: z.string().min(1).max(120),
  models: z.array(seedanceModelCapabilitySchema).min(1),
  ratios: z.array(seedanceRatioSchema).min(1),
  seed: seedanceSeedRangeSchema,
  fileLimitsBytes: seedanceFileLimitsBytesSchema
});

export type SeedanceModelCapability = z.infer<typeof seedanceModelCapabilitySchema>;
export type SeedanceCapabilityCatalog = z.infer<typeof seedanceCapabilityCatalogSchema>;

const imageRoles: readonly ReferenceRole[] = [
  "reference",
  "subject",
  "scene",
  "style",
  "composition",
  "action",
  "camera",
  "first_frame",
  "last_frame",
  "custom"
];
const videoRoles: readonly ReferenceRole[] = [
  "reference",
  "reference_video",
  "subject",
  "scene",
  "style",
  "composition",
  "action",
  "camera",
  "extend_video",
  "custom"
];
const audioRoles: readonly ReferenceRole[] = [
  "reference",
  "audio_reference",
  "custom"
];

export const seedanceCapabilityCatalog: SeedanceCapabilityCatalog =
  seedanceCapabilityCatalogSchema.parse({
    capabilityVersion: "2026-08-18",
    models: [
      {
        id: "doubao-seedance-2-0-260128",
        resolutions: ["480p", "720p", "1080p"],
        durationSeconds: { minimum: 4, maximum: 15, step: 1 },
        allowedRoles: {
          image: imageRoles,
          video: videoRoles,
          audio: audioRoles
        },
        maxReferences: 12,
        maxReferencesByKind: { image: 9, video: 3, audio: 3 },
        supportsAudio: true,
        supportsWatermark: true
      },
      {
        id: "doubao-seedance-2-0-fast-260128",
        resolutions: ["480p", "720p"],
        durationSeconds: { minimum: 4, maximum: 10, step: 1 },
        allowedRoles: {
          image: imageRoles,
          video: videoRoles,
          audio: audioRoles
        },
        maxReferences: 12,
        maxReferencesByKind: { image: 9, video: 3, audio: 3 },
        supportsAudio: true,
        supportsWatermark: true
      }
    ],
    ratios: ["16:9", "21:9", "9:16", "1:1", "4:3", "3:4", "adaptive"],
    seed: { minimum: -1, maximum: 2_147_483_647 },
    fileLimitsBytes: {
      image: 30_000_000,
      video: 50_000_000,
      audio: 15_000_000
    }
  });

export function getSeedanceModelCapability(
  catalog: Readonly<SeedanceCapabilityCatalog>,
  model: SeedanceModel
): SeedanceModelCapability | undefined {
  return catalog.models.find((capability) => capability.id === model);
}

export interface SeedanceSettingsInput {
  model: SeedanceModel;
  ratio: SeedanceRatio;
  durationSeconds: number;
  resolution: SeedanceResolution;
  watermark: boolean;
  generateAudio: boolean;
  seed: number;
}

export interface SeedanceRepair {
  field: "resolution" | "durationSeconds" | "generateAudio" | "watermark";
  previous: string | number | boolean;
  next: string | number | boolean;
  message: string;
}

function nearestDuration(
  value: number,
  range: SeedanceModelCapability["durationSeconds"]
): number {
  const bounded = Math.min(range.maximum, Math.max(range.minimum, value));
  const steps = Math.round((bounded - range.minimum) / range.step);
  return range.minimum + steps * range.step;
}

export function normalizeSeedanceSettings<T extends SeedanceSettingsInput>(
  model: SeedanceModel,
  settings: T,
  catalog: Readonly<SeedanceCapabilityCatalog>
): { settings: T; repaired: readonly SeedanceRepair[] } {
  const capability = getSeedanceModelCapability(catalog, model);
  if (!capability) {
    return { settings: { ...settings, model }, repaired: [] } as { settings: T; repaired: readonly SeedanceRepair[] };
  }
  const repaired: SeedanceRepair[] = [];
  let resolution = settings.resolution;
  let durationSeconds = settings.durationSeconds;
  let generateAudio = settings.generateAudio;
  let watermark = settings.watermark;
  if (!capability.resolutions.includes(resolution)) {
    const next = capability.resolutions.at(-1)!;
    repaired.push({
      field: "resolution",
      previous: resolution,
      next,
      message: `所选模型不支持 ${resolution}，已调整为 ${next}。`
    });
    resolution = next;
  }
  const nextDuration = nearestDuration(durationSeconds, capability.durationSeconds);
  if (nextDuration !== durationSeconds) {
    repaired.push({
      field: "durationSeconds",
      previous: durationSeconds,
      next: nextDuration,
      message: `所选模型支持 ${capability.durationSeconds.minimum}–${capability.durationSeconds.maximum} 秒，已调整时长。`
    });
    durationSeconds = nextDuration;
  }
  if (generateAudio && !capability.supportsAudio) {
    repaired.push({
      field: "generateAudio",
      previous: true,
      next: false,
      message: "所选模型不支持生成音频，已关闭该选项。"
    });
    generateAudio = false;
  }
  if (watermark && !capability.supportsWatermark) {
    repaired.push({
      field: "watermark",
      previous: true,
      next: false,
      message: "所选模型不支持水印选项，已关闭该选项。"
    });
    watermark = false;
  }
  return {
    settings: {
      ...settings,
      model,
      resolution,
      durationSeconds,
      generateAudio,
      watermark
    } as T,
    repaired
  };
}

export interface SeedanceReferenceValidation {
  ready: boolean;
  code?: "UNSUPPORTED_REFERENCE_ROLE";
  message?: string;
}

export function validateSeedanceReference(
  kind: AssetKind,
  role: ReferenceRole,
  capability: Readonly<SeedanceModelCapability>
): SeedanceReferenceValidation {
  if (capability.allowedRoles[kind].includes(role)) {
    return { ready: true };
  }
  return {
    ready: false,
    code: "UNSUPPORTED_REFERENCE_ROLE",
    message: `${capability.id} 不支持将${kind === "image" ? "图片" : kind === "video" ? "视频" : "音频"}设为该引用用途。`
  };
}

export const assetRoleSchema = z.enum([
  "first_frame",
  "last_frame",
  "reference_image",
  "reference_video",
  "reference_audio"
]);
export const seedanceAssetInputSchema = z.strictObject({
  id: z.string(),
  name: z.string(),
  kind: assetKindSchema,
  role: assetRoleSchema,
  url: z.string(),
  sizeBytes: decimalByteCountSchema,
  mimeType: z.string()
});

export type AssetRole = z.infer<typeof assetRoleSchema>;
export type SeedanceAssetInput = z.infer<typeof seedanceAssetInputSchema>;

export interface SeedanceGenerationRequest {
  finalPrompt: string;
  model: SeedanceModel;
  ratio: SeedanceRatio;
  durationSeconds: number;
  resolution: SeedanceResolution;
  watermark: boolean;
  generateAudio: boolean;
  webSearch: boolean;
  seed: number;
  assets: SeedanceAssetInput[];
}

export type SeedanceContent =
  | {
      type: "text";
      text: string;
    }
  | {
      type: "image_url";
      image_url: {
        url: string;
      };
      role?: "first_frame" | "last_frame" | "reference_image";
    }
  | {
      type: "video_url";
      video_url: {
        url: string;
      };
      role: "reference_video";
    }
  | {
      type: "audio_url";
      audio_url: {
        url: string;
      };
      role: "reference_audio";
    };

export interface SeedancePayload {
  model: SeedanceModel;
  content: SeedanceContent[];
  ratio: SeedanceRatio;
  duration: number;
  resolution: SeedanceGenerationRequest["resolution"];
  watermark: boolean;
  generate_audio: boolean;
  seed?: number;
  tools?: Array<{
    type: "web_search";
  }>;
}

export interface SeedanceGenerationResult {
  status: "queued" | "running" | "succeeded" | "failed" | "expired";
  videoUrl?: string;
  usage?: {
    totalTokens?: number;
    completionTokens?: number;
  };
  error?: {
    code?: string;
    message: string;
    requestId?: string;
  };
}

export interface SeedanceProvider {
  uploadAsset(input: {
    filePath: string;
    mimeType: string;
  }): Promise<{
    url: string;
  }>;
  createGeneration(
    input: SeedanceGenerationRequest,
    context: {
      idempotencyKey: string;
    }
  ): Promise<{
    upstreamTaskId: string;
  }>;
  getGeneration(upstreamTaskId: string): Promise<SeedanceGenerationResult>;
}
